import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Counter, counterService } from "../../services/counter.service";
import {
  CounterReportData,
  reportsService,
} from "../../services/reports.service";
import { storeService } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { formatBusinessDate } from "../../lib/utils";

export default function CounterReportPage() {
  const { selectedStoreId } = useAuthStore();
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [filters, setFilters] = useState({
    startDate: formatBusinessDate(new Date()),
    endDate: formatBusinessDate(new Date()),
    storeId: selectedStoreId || "",
    counterId: "",
  });
  const [counters, setCounters] = useState<Counter[]>([]);
  const [reportData, setReportData] = useState<CounterReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    storeService
      .getAll()
      .then(setStores)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadCounters = async () => {
      if (!filters.storeId) {
        setCounters([]);
        return;
      }
      try {
        const response = await counterService.getCounters({
          page: 1,
          limit: 200,
          storeId: filters.storeId,
        });
        setCounters(response.data || []);
      } catch (error) {
        setCounters([]);
      }
    };

    loadCounters();
  }, [filters.storeId]);

  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getCounterReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        storeId: filters.storeId || undefined,
        counterId: filters.counterId || undefined,
      });
      setReportData(data);
    } catch (error) {
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Counter-wise Report
        </h1>
        <p className="text-gray-600">Summary by counter and session</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store
            </label>
            <select
              value={filters.storeId}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  storeId: e.target.value,
                  counterId: "",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Counter
            </label>
            <select
              value={filters.counterId}
              onChange={(e) =>
                setFilters({ ...filters, counterId: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Counters</option>
              {counters.map((counter) => (
                <option key={counter.id} value={counter.id}>
                  {counter.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={loadReport}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && (
        <>
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-lg font-semibold mb-4">Counter Summary</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2">Counter</th>
                    <th className="text-right py-2">Sessions</th>
                    <th className="text-right py-2">Invoices</th>
                    <th className="text-right py-2">Sales</th>
                    <th className="text-right py-2">Returns</th>
                    <th className="text-right py-2">Cash In</th>
                    <th className="text-right py-2">Cash Out</th>
                    <th className="text-right py-2">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.summary.map((row) => (
                    <tr key={row.counterId} className="border-b">
                      <td className="py-2 font-medium text-gray-900">
                        {row.counterName}
                      </td>
                      <td className="py-2 text-right">{row.sessionCount}</td>
                      <td className="py-2 text-right">{row.invoiceCount}</td>
                      <td className="py-2 text-right">
                        {formatCurrency(row.totalSales)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(row.totalReturns)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(row.totalCashIn)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(row.totalCashOut)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(row.variance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Sessions</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2">Session</th>
                    <th className="text-left py-2">Counter</th>
                    <th className="text-left py-2">Date</th>
                    <th className="text-right py-2">Sales</th>
                    <th className="text-right py-2">Cash In</th>
                    <th className="text-right py-2">Cash Out</th>
                    <th className="text-right py-2">Variance</th>
                    <th className="text-right py-2">Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.sessions.map((session) => (
                    <tr key={session.sessionId} className="border-b">
                      <td className="py-2">
                        <Link
                          to={`/billing-sessions/${session.sessionId}`}
                          className="text-blue-600 hover:underline"
                        >
                          {session.sessionNumber}
                        </Link>
                      </td>
                      <td className="py-2">{session.counterName}</td>
                      <td className="py-2">
                        {new Date(session.sessionDate).toLocaleDateString(
                          "en-IN",
                          { timeZone: "Asia/Kolkata" },
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.totalSales)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.totalCashIn)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.totalCashOut)}
                      </td>
                      <td className="py-2 text-right">
                        {formatCurrency(session.variance)}
                      </td>
                      <td className="py-2 text-right">
                        {session.invoiceCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
